import * as v from 'valibot';

/**
 * Something a run made that a person wants to keep.
 *
 * The distinction worth holding onto: a step's *transcript* is how it thought,
 * and an artifact is what it produced. Before this existed the two were the
 * same thing, which meant finding last Tuesday's incident writeup required
 * knowing which step of which run had happened to write it.
 *
 * Versioned rather than overwritten. A run that regenerates a report on retry
 * adds a version; it does not erase the one somebody already read and quoted.
 */
export const ArtifactRecord = v.object({
  id: v.string(),
  runId: v.string(),
  stepId: v.nullable(v.string()),
  workflowId: v.string(),
  /** Stable within a run — `name` plus `version` is the address. */
  name: v.string(),
  version: v.pipe(v.number(), v.integer()),
  contentType: v.string(),
  byteSize: v.pipe(v.number(), v.integer()),
  /** One line saying what it is, written when it is produced. */
  summary: v.nullable(v.string()),
  createdBy: v.nullable(v.string()),
  createdAt: v.string(),
});
export type ArtifactRecord = v.InferOutput<typeof ArtifactRecord>;

/**
 * What a step passes to `reef.artifact()`.
 *
 * `summary` is required rather than optional, and that is the whole design of
 * this call: an artifact list where every row says what it is beats one where
 * every row says `report.md`. The model writing the file is the only thing that
 * knows, and it knows it right now.
 */
export const ArtifactWrite = v.object({
  name: v.pipe(v.string(), v.minLength(1), v.maxLength(120)),
  content: v.string(),
  summary: v.pipe(v.string(), v.minLength(1), v.maxLength(400)),
  contentType: v.optional(v.string(), 'text/markdown'),
});
export type ArtifactWrite = v.InferOutput<typeof ArtifactWrite>;

/** Content types the viewer renders rather than offering as a download. */
export const RENDERABLE_TYPES = [
  'text/markdown',
  'text/plain',
  'application/json',
  'text/csv',
  'text/html',
] as const;

export function isRenderable(contentType: string): boolean {
  return (RENDERABLE_TYPES as readonly string[]).includes(contentType.split(';')[0]!.trim());
}

/**
 * Images the viewer shows as images.
 *
 * Separate from `RENDERABLE_TYPES` because the two are rendered by different
 * elements for different reasons: renderable content goes into a `<pre>`
 * precisely so a model-written artifact cannot put markup on the page, and an
 * image cannot go in a `<pre>` at all. A screenshot the inner loop attached as
 * evidence is the case this exists for — "attach the screenshot" is not
 * satisfied by a link to a file.
 *
 * SVG is deliberately absent. It is the one image type that is a document: it
 * carries script and external references, and an artifact is written by a model
 * out of whatever a tool returned. Everything here decodes to pixels.
 */
export const VIEWABLE_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

export function isViewableImage(contentType: string): boolean {
  return (VIEWABLE_IMAGE_TYPES as readonly string[]).includes(contentType.split(';')[0]!.trim());
}
